import { formatDate } from 'utils/helpers/date'
import ActionCellRenderer from './actionCellRenderer'
import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'

export const getColumns = (
  isEditor: boolean,
  getAggregatorToDelete: (params: ICellRendererParams) => void,
) =>
  [
    { headerName: 'Aggregator Name', field: 'aggregatorName', minWidth: 200 },
    { headerName: 'ID', field: '_id' },
    { headerName: 'User', field: 'username' },
    {
      headerName: 'Creation Date',
      field: 'createdAt',
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value, { includeTime: true }),
    },
    {
      headerName: 'Status',
      field: 'isActive',
      cellRenderer: (params: ICellRendererParams) => {
        return params?.value === true
          ? 'Active'
          : params?.value === false
            ? 'Inactive'
            : '-'
      },
    },
    {
      headerName: 'FTP Status',
      field: 'ftp.isEnable',
      minWidth: 120,
      cellRenderer: (params: ICellRendererParams) => {
        return params?.value === true
          ? 'Enable'
          : params?.value === false
            ? 'Disable'
            : '-'
      },
    },
    {
      headerName: 'API Status',
      field: 'api.isEnable',
      minWidth: 120,
      cellRenderer: (params: ICellRendererParams) => {
        return params?.value === true
          ? 'Enable'
          : params?.value === false
            ? 'Disable'
            : '-'
      },
    },
    {
      hide: !isEditor,
      headerName: 'Action',
      field: 'save',
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams) => (
        <ActionCellRenderer
          params={params}
          isEditor={isEditor}
          getAggregatorToDelete={getAggregatorToDelete}
        />
      ),
    },
  ] as ColDef[]

// Constant: Authentication Types
export const authTypes = {
  BASIC: 'BASIC_AUTH',
  BEARER: 'BEARER_AUTH',
  API_KEY: 'API_KEY_AUTH',
}

// Constant: FTP Protocols
export const FTP_PROTOCOLS = {
  FTP: 'FTP',
  SFTP: 'SFTP',
}

export const deleteFilesOptions = [
  { label: 'Yes', value: true, disabled: false },
  { label: 'No', value: false, disabled: false },
]

// Options: Status options - Active/Inactive
export const activeOptions = [
  { label: 'Active', value: true, disabled: false },
  { label: 'Inactive', value: false, disabled: false },
]

// Options: Authentication
export const authenticationOptions = [
  { label: 'Bearer Auth', value: authTypes.BEARER, disabled: false },
  { label: 'Basic Auth', value: authTypes.BASIC, disabled: false },
  { label: 'API Key Based Auth', value: authTypes.API_KEY, disabled: false },
]

// Options: FTP Protocol
export const ftpProtocolOptions = [
  { label: 'FTP / FTPS', value: FTP_PROTOCOLS.FTP, disabled: false },
  { label: 'SFTP', value: FTP_PROTOCOLS.SFTP, disabled: false },
]

// Constant: Parent keys for API Call
export const aggParentFields = {
  FTP: 'ftp',
  API: 'api',
}

export const aggregatorFormFields = {
  // AGGREGATOR
  AGG_NAME: { key: 'aggregatorName', label: 'Aggregator Name' },
  AGG_ID: { key: 'aggregatorId', label: 'Aggregator ID' },
  AGG_IS_ACTIVE: { key: 'isActive', label: 'Active' },
  // Aggregator FTP
  FTP_ACTIVE: {
    key: 'isEnable',
    label: 'FTP Status',
    parentKey: aggParentFields.FTP,
  },
  FTP_DELETE_FILES: {
    key: 'deleteFiles',
    label: 'Delete old files ?',
    parentKey: aggParentFields.FTP,
  },
  FTP_PROTOCOL: {
    key: 'protocol',
    label: 'Protocol',
    parentKey: aggParentFields.FTP,
  },
  FTP_HOST: {
    key: 'host',
    label: 'Host',
    parentKey: aggParentFields.FTP,
  },
  FTP_PORT: {
    key: 'port',
    label: 'Port',
    parentKey: aggParentFields.FTP,
  },
  FTP_USERNAME: {
    key: 'username',
    label: 'Username',
    parentKey: aggParentFields.FTP,
  },
  FTP_PASSWORD: {
    key: 'password',
    label: 'Password',
    parentKey: aggParentFields.FTP,
  },
  FTP_FILE_GENERATION_LIMIT: {
    key: 'concurrentFileGenerationLimit',
    label: 'File Generation Limit',
    parentKey: aggParentFields.FTP,
  },
  FTP_PATH: {
    key: 'directory',
    label: 'Path',
    parentKey: aggParentFields.FTP,
  },
  // --------------- Aggregator API ---------------
  API_AUTH_TYPE: {
    key: 'authType',
    label: 'Authentication Type',
    parentKey: aggParentFields.API,
  },
  // AUTH - Bearer
  API_AUTH_URL: {
    key: 'authUrl',
    label: 'Login URL',
    parentKey: aggParentFields.API,
  },
  API_AUTH_TOKEN_KEY: {
    key: 'authTokenKey',
    label: 'Token Key',
    parentKey: aggParentFields.API,
  },
  API_AUTH_REQ_SCHEMA: {
    key: 'authRequestSchema',
    label: 'Auth Request Schema',
    parentKey: aggParentFields.API,
  },
  // AUTH - Basic
  API_AUTH_USERNAME: {
    key: 'authUsername',
    label: 'User Name',
    parentKey: aggParentFields.API,
  },
  API_AUTH_PASSWORD: {
    key: 'authPassword',
    label: 'Password',
    parentKey: aggParentFields.API,
  },
  // AUTH - API KEY BASED
  API_AUTH_API_KEY: {
    key: 'authApiKey',
    label: 'Key',
    parentKey: aggParentFields.API,
  },
  API_AUTH_API_VALUE: {
    key: 'authApiValue',
    label: 'Value',
    parentKey: aggParentFields.API,
  },
  // API- Common
  API_ACTIVE: {
    key: 'isEnable',
    label: 'API Status',
    parentKey: aggParentFields.API,
  },
  API_ROOT_SCHEMA: {
    key: 'rootSchema',
    label: 'Root Schema',
    parentKey: aggParentFields.API,
  },
  API_PRODUCT_SCHEMA: {
    key: 'productSchema',
    label: 'Product Schema',
    parentKey: aggParentFields.API,
  },
  API_STORES: {
    key: 'stores',
    label: 'Stores',
    parentKey: aggParentFields.API,
  },
  API_URL: { key: 'url', label: 'API URL', parentKey: aggParentFields.API },
} as const

export const getNestErrorMessage = (
  errors: Record<string, any>,
  obj: Record<string, any>,
): string => {
  return errors?.[obj.parentKey]?.[obj.key]?.message
}

export interface CommonFormType {
  register: UseFormRegister<any>
  errors: FieldErrors<any>
  setValue: UseFormSetValue<any>
  aggregator?: any
  ftpIsActive?: boolean
  apiIsActive?: boolean
}
